import React, { useEffect, useState } from "react";
import {
	Row,
	Col,
	Button,
	Space,
	Input,
	Drawer,
	notification,
	Popconfirm,
} from "antd";
import { HistoryOutlined, PlusOutlined } from "@ant-design/icons";
import {
	ApprovalButton,
	DeclineButton,
	DelegateButton,
	RespondButton,
	QueryButton,
	WorkflowTypeContainer,
} from "./Components";
import { useDispatch, useSelector } from "react-redux";
import {
	fetchFromTemplate,
	submit,
	approve,
	decline,
	resume,
	delegate,
	query,
	deleteAction,
	selectAllWorkflows,
	selectWorkflowStatus,
	edit,
	selectWorkflowsError,
	workflowErrorEdited,
	selectActiveWorkflowQuery,
	respond,
	deleteWorkflowsByFormId,
	selectWorkflowTypes,
	add,
} from "reducers/workflowsSlice";
import { selectForm } from "reducers";
import {
	selectDelegateUsers,
	selectIsSysAdmin,
	selectUser,
	selectUserOrganisation,
} from "reducers/securitySlice";
import { selectFormDefinition, selectFormOwner } from "reducers/formSlice";
import Comment from "./Comment";
import { selectLastSubmit } from "reducers/workflowHistorySlice";
import { Section } from "components/QnaComponents";
import WorkflowHistory from "./WorkflowHistory";
import { selectGlobal } from "reducers/global";
import { getTimezone } from "utils/common";
import dayjs from "dayjs";

const initialApprovalState = {
	isApproval: false,
	approvalType: "",
	initialValues: { userId: null, comments: null },
	defaultOptions: [], //to prepopulate userId
	approvalDispatch: () => {},
};

const { TextArea } = Input;

const Approval = ({
	templateCode,
	organisationHierarchyId,
	error,
	conditions,
	options,
	domain,
	isValid = true,
	validationMessage,
}) => {
	const [drawerVisible, setDrawerVisible] = useState(false);

	//const populateFromTemplate = false; //to test populate from template or get from exisitng workflow
	const dispatch = useDispatch();

	//form and def data for logic
	const form = useSelector(selectForm);
	const formOwner = useSelector(selectFormOwner);
	const delegateUsers = useSelector(selectDelegateUsers);
	const user = useSelector(selectUser);
	const organisation = useSelector(selectUserOrganisation);
	const { status, id: formId } = form;
	const definition = useSelector(selectFormDefinition);
	const formDefinitionId = definition.id;
	const isSysAdmin = useSelector(selectIsSysAdmin);
	const { appType } = useSelector(selectGlobal);

	//logic for go/don't go for posting form and workflows
	const workflowStatus = useSelector(selectWorkflowStatus);

	//logic for when to show approval comments

	const [approvalState, setApprovalState] = useState(initialApprovalState);

	const approvalCancel = () => {
		setApprovalState(initialApprovalState);
	};

	//data for displaying workflows and workflow actions
	const workflows = useSelector(selectAllWorkflows);
	const activeWorkflow = workflows?.find((p) => p.isActive);

	const activeWorkflowQuery = useSelector(selectActiveWorkflowQuery);
	const isRespondEnabled =
		activeWorkflowQuery &&
		(activeWorkflowQuery?.user?.id === user?.id || isSysAdmin);

	//for submit to show up, form must be in Draft and there must be workflows present
	//and logged in user must be the form owner
	//OR be sys admin
	const isSubmitEnabled =
		(user?.id === formOwner?.id || formOwner === null || isSysAdmin) &&
		(status === "Draft" ||
			status === "Cancelled" ||
			status === "Declined") &&
		workflows?.length > 0;

	//for approval buttons to show up, active workflow and current user must match
	//OR they must have delegated access for that user
	//OR be sys admin
	const isApprovalEnabled =
		(user?.id === activeWorkflow?.userId ||
			delegateUsers.some((u) => u.id === activeWorkflow?.userId) ||
			isSysAdmin) &&
		activeWorkflow &&
		user &&
		!isRespondEnabled && //if query, response is the only available option
		!isSubmitEnabled; //migration bug where workflows are marked as active, even in draft forms

	//for resume to show up, form must be in Declined and there must be workflows present
	//and logged in user must be the form ownser
	const isResumeEnabled = false;

	//check if no workflows exist, so we can load new ones
	const isWorkflowEmpty = !workflows || workflows.length === 0;

	//the submitter's comments are found on the last submit workflow history
	const lastSubmitWorkflowHistory = useSelector(selectLastSubmit);

	//for showing workflow related errors
	const workflowError = useSelector(selectWorkflowsError);

	useEffect(() => {
		if (workflowError) {
			notification.error({
				message: workflowError?.title,
				description: workflowError?.message,
				duration: 0,
				key: workflowError?.title, //ensure only one error message per type can be shown
				onClose: () => dispatch(workflowErrorEdited(null)),
			});
		}
	}, [workflowError, dispatch]);

	useEffect(() => {
		//if workflows do not exists, fetch by template
		if (
			isWorkflowEmpty &&
			workflowStatus !== "loading" &&
			workflowStatus !== "failed"
		) {
			dispatch(
				fetchFromTemplate({
					formGuid: formId,
					organisationHierarchyId,
					formDefinitionId,
					conditions,
					code: templateCode,
					domain,
					organisation,
				})
			);
		}
	}, [
		isWorkflowEmpty,
		workflowStatus,
		templateCode,
		formId,
		organisationHierarchyId,
		formDefinitionId,
		conditions,
		domain,
		organisation,
		dispatch,
	]);

	const onRestart = async () => {
		await dispatch(deleteWorkflowsByFormId({ responseFormId: formId }));
	};

	const onEdit = ({ workflow, roleId, organisationId }) => {
		setApprovalState({
			isApproval: true,
			approvalType: "Change",
			initialValues: {
				userId: user.id,
			},
			defaultOptions: [
				{
					roleId,
					organisationId,
				},
			],
			approvalDispatch: ({ comments, changeUserId }) => {
				dispatch(
					edit({
						workflow: { ...workflow, userId: changeUserId },
						comments,
					})
				);
			},
		});
	};

	const onDelete = ({ workflow }) => {
		setApprovalState({
			isApproval: true,
			approvalType: "Delete",
			approvalDispatch: ({ comments }) => {
				dispatch(
					deleteAction({
						workflow: { ...workflow, isDeleted: true },
						comments,
					})
				);
			},
		});
	};

	const types = useSelector(selectWorkflowTypes);

	return (
		<>
			<Drawer
				open={drawerVisible}
				closable
				onClose={() => {
					setDrawerVisible(false);
				}}
				width="100%"
				drawerStyle={{
					backgroundColor: "rgba(245, 245, 245, 0.6)",
				}}
			>
				<WorkflowHistory />
				<Row justify="end" style={{ marginTop: 20, maxWidth: 600 }}>
					<Col>
						<Button
							type="default"
							onClick={() => {
								setDrawerVisible(false);
							}}
						>
							Close
						</Button>
					</Col>
				</Row>
			</Drawer>
			<Section size={options?.size}>
				{!["Draft", "Cancelled", "Declined"].includes(status) && (
					<>
						<h3>Comments from Submitter</h3>
						<TextArea
							value={
								lastSubmitWorkflowHistory?.comment ||
								"No comments"
							}
							style={{ marginBottom: 20 }}
							disabled
						/>
					</>
				)}
				<Comment
					open={approvalState.isApproval}
					type={approvalState.approvalType}
					onOk={approvalState.approvalDispatch}
					initialValues={approvalState.initialValues}
					defaultOptions={approvalState.defaultOptions}
					onCancel={approvalCancel}
				/>
				<Row justify="space-between">
					<Col>
						<h3>Approval Workflow</h3>
					</Col>
					<Col>
						<Space>
							<Button
								type="link"
								onClick={() => {
									setDrawerVisible(true);
								}}
							>
								<HistoryOutlined />
								View Approval Log
							</Button>
							{options?.enableReset && isSubmitEnabled && (
								<Popconfirm
									title="Are you sure you want to reset this form's workflows? This will delete all existing workflows and their approvals and recreate them."
									onConfirm={onRestart}
								>
									<Button danger>Reset</Button>
								</Popconfirm>
							)}
						</Space>
					</Col>
				</Row>
				{lastSubmitWorkflowHistory?.createOn && (
					<Row>
						<Col style={{ fontWeight: 300 }}>
							Submitted on{" "}
							{getTimezone() === "UTC"
								? dayjs
										.utc(
											lastSubmitWorkflowHistory?.createOn
										)
										.local()
										.format("DD/MM/YYYY hh:mma")
								: dayjs(
										lastSubmitWorkflowHistory?.createOn
								  ).format("DD/MM/YYYY hh:mma")}
						</Col>
					</Row>
				)}
				{types.length > 0 ? (
					types.map((type) => (
						<WorkflowTypeContainer
							workflows={workflows.filter((w) => w.type === type)}
							organisationHierarchyId={organisationHierarchyId}
							onEdit={onEdit}
							onDelete={onDelete}
							options={options}
							type={type}
							key={type}
						/>
					))
				) : (
					<WorkflowTypeContainer
						workflows={workflows}
						organisationHierarchyId={organisationHierarchyId}
						onEdit={onEdit}
						onDelete={onDelete}
						options={options}
						type={null}
					/>
				)}
				{error ? (
					<div style={{ marginTop: 20, color: "#555" }}>{error}</div>
				) : (
					<div className="workflow-buttons-container">
						<Space wrap>
							{!isValid && isSubmitEnabled && (
								<div className="qna-question-form-item-validation">
									{validationMessage}
								</div>
							)}
							{(isSubmitEnabled || isApprovalEnabled) &&
								options?.enableAdd && (
									<Button
										size="large"
										icon={<PlusOutlined />}
										onClick={() =>
											setApprovalState({
												isApproval: true,
												approvalType: "Add",
												approvalDispatch: ({
													userId,
													roleId,
													type,
													sequence,
													comments,
												}) => {
													dispatch(
														add({
															responseFormId:
																formId,
															userId,
															roleId,
															type,
															sequence,
															comments,
														})
													);
												},
											})
										}
									>
										Add Workflow
									</Button>
								)}
							{isSubmitEnabled && (
								<Button
									type="primary"
									size="large"
									disabled={!isValid}
									onClick={() =>
										setApprovalState({
											isApproval: true,
											approvalType:
												appType === "cme"
													? "SubmitCme"
													: "Submit",
											approvalDispatch: ({
												comments,
											}) => {
												dispatch(
													submit({
														workflows,
														comments,
													})
												);
											},
										})
									}
								>
									Submit
								</Button>
							)}
							{isResumeEnabled && (
								<Button
									type="primary"
									size="large"
									onClick={() =>
										setApprovalState({
											isApproval: true,
											approvalType: "Resume",
											approvalDispatch: ({
												comments,
											}) => {
												dispatch(
													resume({
														workflows,
														comments,
													})
												);
											},
										})
									}
								>
									Resubmit
								</Button>
							)}
							{isRespondEnabled ? (
								<RespondButton
									onClick={() => {
										setApprovalState({
											isApproval: true,
											approvalType: "Respond",
											approvalDispatch: ({
												comments,
											}) => {
												dispatch(
													respond({
														workflow:
															activeWorkflow,
														comments,
													})
												);
											},
										});
									}}
								/>
							) : isApprovalEnabled ? (
								<>
									<ApprovalButton
										onClick={() => {
											setApprovalState({
												isApproval: true,
												approvalType:
													appType === "cme"
														? "ApproveCme"
														: "Approve",
												approvalDispatch: ({
													comments,
												}) => {
													dispatch(
														approve({
															workflow:
																activeWorkflow,
															comments,
														})
													);
												},
											});
										}}
									/>
									<DeclineButton
										onClick={() =>
											setApprovalState({
												isApproval: true,
												approvalType: "Decline",
												approvalDispatch: ({
													comments,
												}) => {
													dispatch(
														decline({
															workflow:
																activeWorkflow,
															comments,
														})
													);
												},
											})
										}
									/>
									<DelegateButton
										onClick={() =>
											setApprovalState({
												isApproval: true,
												approvalType: "Delegate",
												approvalDispatch: ({
													comments,
													userId,
												}) => {
													dispatch(
														delegate({
															workflow:
																activeWorkflow,
															delegateUserId:
																userId,
															comments,
														})
													);
												},
											})
										}
									/>
									{appType === "cme" && (
										<QueryButton
											onClick={() =>
												setApprovalState({
													isApproval: true,
													approvalType: "Query",
													initialValues: {
														userId: formOwner.id,
													},
													defaultOptions: [
														{
															value: formOwner.id,
															text: formOwner.fullName,
														},
													],
													approvalDispatch: ({
														comments,
														userId,
													}) => {
														dispatch(
															query({
																workflow:
																	activeWorkflow,
																delegateUserId:
																	userId,
																comments,
															})
														);
													},
												})
											}
										/>
									)}
								</>
							) : (
								<></>
							)}
						</Space>
					</div>
				)}
			</Section>
		</>
	);
};

export default Approval;
